'use client'
import React, { useState, useEffect, useRef } from 'react'
import { useRouter, useSearchParams, usePathname } from 'next/navigation'
import LargeNewDevelopmentCard from '@components/LargeNewDevelopmentCard'
import dynamic from 'next/dynamic'
const SearchProjectMap = dynamic(() => import('@components/SearchProjectMap'), { ssr: false })
import MobileSearchProjectMap from '@components/MobileSearchProjectMap'
import MobileMapProjectCard from '@components/MobileMapProjectCard'
import { getFilteredProjects } from '@services/getFilteredProjects'
import { getProjectCardInfo } from '@services/getProjectCardInfo'
import { useSession } from 'next-auth/react'

import { Swiper, SwiperSlide } from 'swiper/react'
import 'swiper/css'
import 'swiper/css/navigation'

const SearchProjectResultCards = ({ searchParams, cardStyle, mapCardStyle, mobileCardStyle, dictionary, lang = 'en', scrollMode = 'pagination', searchPageConfig }) => {

    // ── Resolve config ─────────────────────────────────────────────────────────
    const cfg                = searchPageConfig ?? {}
    const resolvedScrollMode = cfg.scrollMode   ?? scrollMode

    const colsDesktop  = cfg.colsDesktop ?? 4
    const colsTablet   = cfg.colsTablet  ?? 2
    const colsMobile   = cfg.colsMobile  ?? 1
    const cardGap      = cfg.cardGap     ?? 12

    const btnBg        = cfg.btnBg               ?? 'var(--thirdcolor)'
    const btnTextColor = cfg.btnTextColor         ?? '#ffffff'
    const btnRadius    = cfg.btnRadius            ?? 6
    const pActiveBg    = cfg.paginationActiveBg   ?? 'var(--thirdcolor)'
    const pActiveTc    = cfg.paginationActiveText ?? '#ffffff'
    const pBorderColor = cfg.paginationBorderColor ?? '#e5e7eb'

    const mapCfg         = cfg.map ?? {}
    const splitRatio     = mapCfg.splitRatio     ?? 40
    const sidebarSide    = mapCfg.sidebarSide    ?? 'left'
    const mapColsDesktop = mapCfg.mapColsDesktop ?? 1

    // ── Auth ───────────────────────────────────────────────────────────────────
    const { data: session } = useSession()
    let userEmail = null
    if (session !== null) userEmail = session?.user?.email

    // ── Routing ────────────────────────────────────────────────────────────────
    const router    = useRouter()
    const pathname  = usePathname()
    const urlParams = useSearchParams()

    // ── State ──────────────────────────────────────────────────────────────────
    const [page, setPage]                            = useState(resolvedScrollMode === 'pagination' ? (Number(urlParams.get('page')) || 1) : 1)
    const [allPages, setAllPages]                    = useState(1)
    const [projects, setProjects]                    = useState([])
    const [propertyCount, setPropertyCount]          = useState(0)
    const [showMap, setShowMap]                      = useState(false)
    const [coordinates, setCoordinates]              = useState([])
    const [hoveredProperty, setHoveredProperty]      = useState(null)
    const [activeProperty, setActiveProperty]        = useState({})
    const [activePropertyLat, setActivePropertyLat] = useState(13.732772)
    const [activePropertyLng, setActivePropertyLng] = useState(100.5801953)

    const swiperRef   = useRef(null)
    const projectsRef = useRef([])

    // ── Handlers ───────────────────────────────────────────────────────────────
    const handleMouseEnter = (id) => setHoveredProperty(id)
    const handleMouseLeave = () => setHoveredProperty(null)

    const handleSlideChange = (swiper) => {
        const idx   = swiper.activeIndex
        const slide = swiper.slides[idx]
        const lat   = Number(slide.dataset.latitude)
        const lng   = Number(slide.dataset.longitude)
        setActiveProperty(slide.id)
        if (isFinite(lat) && lat !== 0) setActivePropertyLat(lat)
        if (isFinite(lng) && lng !== 0) setActivePropertyLng(lng)
    }

    const handleMarkerClick = async ({ _id, latitude, longitude }) => {
        setActiveProperty(_id)
        setActivePropertyLat(Number(latitude))
        setActivePropertyLng(Number(longitude))
        const idx = projectsRef.current.findIndex(p => p.id === _id || p._id === _id)
        if (idx !== -1) { swiperRef.current?.slideTo(idx); return }
        try {
            const fetched = await getProjectCardInfo(_id)
            if (!fetched || fetched.message) return
            fetched.latitude  = latitude
            fetched.longitude = longitude
            const next = [fetched, ...projectsRef.current]
            projectsRef.current = next
            setProjects(next)
            setTimeout(() => swiperRef.current?.slideTo(0), 50)
        } catch (e) { console.error('getProjectCardInfo failed', e) }
    }

    // ── Data ───────────────────────────────────────────────────────────────────
    const getData = async (pageNumber) => {
        const result = await getFilteredProjects({
            category:       searchParams.category       || '',
            districts:      searchParams.districts      || '',
            petfriendly:    searchParams.petfriendly    || '',
            newdevelopment: searchParams.newdevelopment || '',
            district:       searchParams.district || '',
            location:       searchParams.location || '',
            bts:            searchParams.bts || '',
            name:           searchParams.name || '',
            seaview:        searchParams.seaview || '',
            beachfront:     searchParams.beachfront || '',
            map:            searchParams.map            || false,
            sort:           searchParams.sort           || 'datedesc',
            limit:          searchParams.limit || 8,
            page:           pageNumber,
        }, userEmail)

        if (pageNumber > 1 && resolvedScrollMode !== 'pagination') {
            const next = [...projectsRef.current, ...result.projects]
            projectsRef.current = next
            setProjects(next)
        } else {
            projectsRef.current = result.projects
            setProjects(result.projects)
        }
        setAllPages(result.pages)
        setPropertyCount(result.count)
        setCoordinates(result.coordinates)
        if (result.projects.length > 0 && pageNumber === 1) {
            setActiveProperty(result.projects[0].id)
            setActivePropertyLat(Number(result.projects[0].latitude))
            setActivePropertyLng(Number(result.projects[0].longitude))
        }
    }

    useEffect(() => {
        const urlPage = resolvedScrollMode === 'pagination' ? (Number(urlParams.get('page')) || 1) : 1
        setPage(urlPage)
        setProjects([])
        setShowMap(searchParams?.map)
        getData(urlPage)
    }, [searchParams])

    useEffect(() => { getData(page) }, [page])

    // ── Pagination ─────────────────────────────────────────────────────────────
    const goToPage = (p) => {
        const next = Math.max(1, Math.min(allPages, p))
        setPage(next)
        const params = new URLSearchParams(urlParams.toString())
        if (next === 1) params.delete('page')
        else params.set('page', String(next))
        router.push(`${pathname}?${params.toString()}`, { scroll: true })
    }

    const PaginationControls = () => {
        if (resolvedScrollMode !== 'pagination' || allPages <= 1) return null
        const getPageNumbers = () => {
            if (allPages <= 7) return Array.from({ length: allPages }, (_, i) => i + 1)
            if (page <= 4)            return [1, 2, 3, 4, 5, '...', allPages]
            if (page >= allPages - 3) return [1, '...', allPages - 4, allPages - 3, allPages - 2, allPages - 1, allPages]
            return [1, '...', page - 1, page, page + 1, '...', allPages]
        }
        const baseBtn = { padding: '6px 10px', borderRadius: btnRadius, background: '#fff', color: '#374151', fontSize: 13, cursor: 'pointer', border: `1px solid ${pBorderColor}` }
        return (
            <div style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, paddingTop: 32, paddingBottom: 32 }}>
                <button disabled={page === 1} onClick={() => goToPage(page - 1)} style={{ ...baseBtn, opacity: page === 1 ? 0.3 : 1 }}>‹</button>
                {getPageNumbers().map((p, i) =>
                    p === '...'
                        ? <span key={`e-${i}`} style={{ padding: '6px 4px', fontSize: 13, color: '#9ca3af' }}>…</span>
                        : <button key={p} onClick={() => goToPage(p)} style={{ ...baseBtn, border: `1px solid ${p === page ? pActiveBg : pBorderColor}`, background: p === page ? pActiveBg : '#fff', color: p === page ? pActiveTc : '#374151', fontWeight: p === page ? 700 : 400 }}>{p}</button>
                )}
                <button disabled={page === allPages} onClick={() => goToPage(page + 1)} style={{ ...baseBtn, opacity: page === allPages ? 0.3 : 1 }}>›</button>
            </div>
        )
    }

    const LoadMoreButton = () => {
        if (resolvedScrollMode !== 'loadmore' || page >= allPages) return null
        return (
            <div style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', paddingTop: 32, paddingBottom: 32 }}>
                <button onClick={() => setPage(p => p + 1)}
                    style={{ width: 300, padding: '8px 16px', background: btnBg, color: btnTextColor, borderRadius: btnRadius, border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 14 }}>
                    {dictionary?.property?.loadMore ?? 'Load More'}
                </button>
            </div>
        )
    }

    const newLimit = () => {
        if (resolvedScrollMode === 'infinite' && page < allPages) setPage(p => p + 1)
    }

    // ── Map sidebar/panel styles ───────────────────────────────────────────────
    const sidebarStyle = {
        flexBasis:   `${splitRatio}%`,
        flexShrink:  0,
        order:       sidebarSide === 'right' ? 2 : 0,
        borderRight: sidebarSide === 'left'  ? '1px solid #e5e7eb' : 'none',
        borderLeft:  sidebarSide === 'right' ? '1px solid #e5e7eb' : 'none',
    }
    const mapPanelStyle = {
        flex:  1,
        order: sidebarSide === 'right' ? 0 : 2,
    }

    // ── Render ─────────────────────────────────────────────────────────────────
    return (
        <div className='container-wide sm:min-h-[80vh] mx-auto lg:px-3 sm:px-0 px-3'>
            {showMap ? (
                <div className='w-full relative flex flex-row mx-auto'>

                    {/* Desktop sidebar */}
                    <div
                        className='md:flex hidden md:h-[80vh] h-full pt-2 overflow-hidden overflow-y-scroll flex-col items-center scrollable'
                        style={sidebarStyle}
                    >
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: `repeat(${mapColsDesktop}, 1fr)`,
                            gap: 8,
                            width: '100%',
                        }}>
                            {projects.map((project, index) => (
                                <div key={project.id} id={project.id}
                                    onMouseEnter={() => handleMouseEnter(project.id)}
                                    onMouseLeave={handleMouseLeave}
                                    className='mb-2 w-full'
                                >
                                    <LargeNewDevelopmentCard
                                        dictionary={dictionary.card}
                                        lang={lang}
                                        project={project}
                                        isLast={index === projects.length - 1}
                                        newLimit={newLimit}
                                        cardStyle={cardStyle}
                                    />
                                </div>
                            ))}
                        </div>
                        <LoadMoreButton />
                        <PaginationControls />
                    </div>

                    {/* Mobile swiper — pointer-events:none so map markers stay tappable */}
                    <div className='md:hidden block absolute bottom-0 left-0 right-0 z-[52]' style={{ pointerEvents: 'none' }}>
                        <div className='sm:hidden block' style={{ pointerEvents: 'auto' }}>
                            <Swiper slidesPerView={1} onSwiper={s => { swiperRef.current = s }} onSlideChange={handleSlideChange}>
                                {projects.map((project, index) => (
                                    <SwiperSlide
                                        key={project.id}
                                        id={project.id}
                                        data-latitude={Number(project.latitude)}
                                        data-longitude={Number(project.longitude)}
                                    >
                                        <MobileMapProjectCard
                                            lang={lang}
                                            dictionary={dictionary.card}
                                            project={project}
                                            isLast={index === projects.length - 1}
                                            newLimit={newLimit}
                                            cardStyle={mobileCardStyle}
                                        />
                                    </SwiperSlide>
                                ))}
                            </Swiper>
                        </div>
                    </div>

                    {/* Desktop map */}
                    <div className='md:block hidden md:h-[80vh] h-full md:relative absolute overflow-hidden' style={mapPanelStyle}>
                        <SearchProjectMap
                            coordinates={coordinates}
                            hoveredProperty={hoveredProperty}
                            latitude={projects?.[0]?.latitude}
                            longitude={projects?.[0]?.longitude}
                            lang={lang}
                            dictionary={dictionary}
                            mapConfig={mapCfg}
                            cardStyle={mapCardStyle}
                        />
                    </div>

                    {/* Mobile map */}
                    <div className='md:hidden block w-full h-full z-[51]'>
                        <MobileSearchProjectMap
                            coordinates={coordinates}
                            hoveredProperty={activeProperty}
                            zoomControl={false}
                            latitude={activePropertyLat}
                            longitude={activePropertyLng}
                            lang={lang}
                            dictionary={dictionary}
                            mapConfig={mapCfg}
                            cardStyle={mapCardStyle}
                            onMarkerClick={handleMarkerClick}
                        />
                    </div>
                </div>
            ) : (
                <>
                    <style>{`
                        #proj-grid {
                            display: grid;
                            width: 100%;
                            gap: ${cardGap}px;
                            grid-template-columns: repeat(${colsMobile}, 1fr);
                            align-items: start;
                        }
                        #proj-grid > * {
                            min-width: 0;
                            width: 100%;
                        }
                        @media (min-width: 640px) {
                            #proj-grid { grid-template-columns: repeat(${colsTablet}, 1fr); }
                        }
                        @media (min-width: 1024px) {
                            #proj-grid { grid-template-columns: repeat(${colsDesktop}, 1fr); }
                        }
                    `}</style>
                    <div id="proj-grid">
                        {projects.map((project, index) => (
                            <LargeNewDevelopmentCard
                                key={project.id}
                                dictionary={dictionary.card}
                                lang={lang}
                                project={project}
                                isLast={index === projects.length - 1}
                                newLimit={newLimit}
                                cardStyle={cardStyle}
                            />
                        ))}
                    </div>
                    <LoadMoreButton />
                    <PaginationControls />
                </>
            )}
        </div>
    )
}

export default SearchProjectResultCards